import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input } from "antd";

import { useDispatch } from "react-redux";
import { authValidate, authRequest } from "../../redux/actions/authAction";
import { workEmailValidator } from "../../utils/workEmail";

const RESEND_COOLDOWN_SECONDS = 60;

export default function FormLogin({
  lockedEmail,
  onAuthenticated,
  onAccountNotFound,
  compact = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [generateOTP, setGenerateOTP] = useState(false);
  const [usePasswordLogin, setUsePasswordLogin] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendCountdownActive, setResendCountdownActive] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (lockedEmail) {
      form.setFieldsValue({ emailAddress: lockedEmail });
    }
  }, [lockedEmail, form]);

  const startResendCountdown = () => {
    setResendCountdown(RESEND_COOLDOWN_SECONDS);
    setResendCountdownActive(true);
  };

  useEffect(() => {
    if (!resendCountdownActive || resendCountdown <= 0) return;
    const id = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          setResendCountdownActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resendCountdownActive, resendCountdown]);

  const finishAuth = () => {
    if (onAuthenticated) {
      Promise.resolve(onAuthenticated())
        .then(() => setLoading(false))
        .catch(() => setLoading(false));
      return;
    }
    setTimeout(() => {
      navigate("/app");
      setLoading(false);
      window.location.reload();
    }, 1000);
  };

  const handleAuthRequestError = (error) => {
    const code = error?.response?.data?.error?.code;
    const msg = String(error?.response?.data?.message || "");
    if (
      onAccountNotFound &&
      (code === "ACCOUNT_NOT_FOUND" || msg.includes("No account found"))
    ) {
      onAccountNotFound();
    }
  };

  const handleResendOTP = () => {
    const emailAddress = lockedEmail || form.getFieldValue("emailAddress");
    if (!emailAddress || resendCountdown > 0) return;
    setLoading(true);
    dispatch(authRequest({ emailAddress }))
      .then(() => {
        startResendCountdown();
        setLoading(false);
      })
      .catch((error) => {
        handleAuthRequestError(error);
        setTimeout(() => setLoading(false), 1000);
      });
  };

  const onSubmit = (values) => {
    setLoading(true);
    if (usePasswordLogin) {
      dispatch(
        authValidate({
          emailAddress: values.emailAddress,
          password: values.password,
        }),
      )
        .then(finishAuth)
        .catch(() => {
          setTimeout(() => setLoading(false), 1000);
        });
    } else if (generateOTP) {
      dispatch(authValidate(values))
        .then(finishAuth)
        .catch(() => {
          setTimeout(() => setLoading(false), 1000);
        });
    } else {
      dispatch(authRequest(values))
        .then(() => {
          setGenerateOTP(true);
          startResendCountdown();
          setLoading(false);
        })
        .catch((error) => {
          handleAuthRequestError(error);
          setTimeout(() => setLoading(false), 1000);
        });
    }
  };

  return (
    <Form
      form={form}
      initialValues={lockedEmail ? { emailAddress: lockedEmail } : {}}
      onFinish={onSubmit}
      layout="vertical"
      style={
        compact
          ? { marginTop: 0, width: "100%" }
          : { marginTop: "3rem", width: "min(400px, calc(100vw - 3rem))" }
      }
    >
      <Form.Item
        name="emailAddress"
        rules={[
          {
            required: true,
            message: "Please input your email address!",
          },
          { type: "email", message: "Please enter a valid email address" },
          { validator: workEmailValidator },
        ]}
      >
        <Input
          placeholder="Email Address"
          disabled={Boolean(lockedEmail)}
          onChange={() => setGenerateOTP(false)}
        />
      </Form.Item>
      {usePasswordLogin && (
        <Form.Item
          name="password"
          rules={[
            {
              required: true,
              message: "Please input your password!",
            },
          ]}
        >
          <Input.Password placeholder="Password" />
        </Form.Item>
      )}
      {!usePasswordLogin && generateOTP && (
        <Form.Item name="oneTimePassword">
          <Input.OTP
            placeholder="One Time Password"
            disabled={!generateOTP}
            size="large"
            style={{ width: "100%" }}
          />
        </Form.Item>
      )}
      {!usePasswordLogin && generateOTP && (
        <Form.Item style={{ marginBottom: 16 }}>
          <Button
            type="link"
            onClick={handleResendOTP}
            disabled={resendCountdown > 0 || isLoading}
            style={{ padding: 0 }}
          >
            {resendCountdown > 0
              ? `Resend OTP in ${resendCountdown}s`
              : "Resend OTP"}
          </Button>
        </Form.Item>
      )}
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading} block>
          {usePasswordLogin ? "Login" : generateOTP ? "Login" : "Generate OTP"}
        </Button>
      </Form.Item>
      {!compact ? (
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="link"
            block
            onClick={() => {
              const nextUsePasswordLogin = !usePasswordLogin;
              setUsePasswordLogin(nextUsePasswordLogin);
              if (nextUsePasswordLogin) {
                setGenerateOTP(false);
                setResendCountdown(0);
                setResendCountdownActive(false);
                form.setFieldsValue({ oneTimePassword: undefined });
              } else {
                form.setFieldsValue({ password: undefined });
              }
            }}
          >
            {usePasswordLogin
              ? "Login via One Time Password"
              : "Login with Password"}
          </Button>
        </Form.Item>
      ) : null}
    </Form>
  );
}
